import './ImageFileCrop.scss';
import { MimeType } from '@sonar-software/sonar-ts-types';
import { Slider } from 'antd';
import { useOnUnmount } from '@hooks';
import { createCroppedImage, readDataFromFile } from '@sonar-software/sonar-ts-lib';
import Cropper, { Area, MediaSize, Point, Size } from 'react-easy-crop';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { RotateLeftOutlined, RotateRightOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';

const defaultCrop: Point = {
	x: 0,
	y: 0
};

const defaultCropSize: Size = {
	height: 0,
	width: 0
};

const defaultArea: Area = {
	height: 0,
	width: 0,
	x: 0,
	y: 0
};

const defaultRotation = 0;
const minZoom = 1;
const maxZoom = 5;
const minRotate = -180;
const maxRotate = 180;

export interface ImageFileCropProps {
	aspect?: number;
	cropShape?: 'rect' | 'round';
	sourceFile: File | null | undefined;
}

export interface ImageFileCropRef {
	getCroppedImage: () => Promise<Blob | null>;
}

export const ImageFileCrop = forwardRef<ImageFileCropRef, ImageFileCropProps>(
	({ aspect = 1, cropShape = 'rect', sourceFile }, ref) => {
		const [ crop, setCrop ] = useState(defaultCrop);
		const [ cropSize, setCropSize ] = useState(defaultCropSize);
		const [ rotation, setRotation ] = useState(defaultRotation);
		const [ zoom, setZoom ] = useState(minZoom);
		const [ image, setImage ] = useState<string>();

		const imageTypeRef = useRef<MimeType>();
		const cropAreaRef = useRef(defaultArea);

		useEffect(() => {
			async function processFile() {
				if (!sourceFile) {
					return;
				}

				const imageData = await readDataFromFile(sourceFile);

				if (typeof imageData === 'string') {
					imageTypeRef.current = sourceFile.type as MimeType;

					setImage(imageData);
				}
			}

			processFile();
		}, [ sourceFile ]);

		useOnUnmount(() => {
			if (image) {
				URL.revokeObjectURL(image);
			}
		});

		useImperativeHandle(
			ref,
			() => ({
				getCroppedImage: () => {
					if (image) {
						return createCroppedImage(image, imageTypeRef.current, cropAreaRef.current, rotation);
					}

					return Promise.resolve(null);
				}
			}),
			[ image, rotation ]
		);

		const handleMediaLoaded = useCallback(
			(mediaSize: MediaSize) => {
				const { width, height } = mediaSize;
				const ratioWidth = height * aspect;

				if (width > ratioWidth) {
					setCropSize({
						height,
						width: ratioWidth
					});
				} else {
					setCropSize({
						height: width / aspect,
						width
					});
				}
			},
			[ aspect ]
		);

		const handleCropComplete = useCallback((_: Area, cropAreaPixels: Area) => {
			cropAreaRef.current = cropAreaPixels;
		}, []);

		return (
			<section className='image-file-crop'>
				<div className='image-file-crop-container'>
					<Cropper
						aspect={ aspect }
						crop={ crop }
						cropShape={ cropShape }
						cropSize={ cropSize }
						image={ image }
						maxZoom={ maxZoom }
						minZoom={ minZoom }
						onCropChange={ setCrop }
						onCropComplete={ handleCropComplete }
						onMediaLoaded={ handleMediaLoaded }
						onRotationChange={ setRotation }
						onZoomChange={ setZoom }
						rotation={ rotation }
						zoom={ zoom } />
				</div>
				<div className='image-file-crop-controls'>
					<div>
						<ZoomOutOutlined />
						<Slider
							defaultValue={ minZoom }
							max={ maxZoom }
							min={ minZoom }
							onChange={ setZoom }
							step={ 0.1 }
							value={ zoom } />
						<ZoomInOutlined />
					</div>
					<div>
						<RotateLeftOutlined />
						<Slider
							defaultValue={ 0 }
							max={ maxRotate }
							min={ minRotate }
							onChange={ setRotation }
							step={ 1 }
							value={ rotation } />
						<RotateRightOutlined />
					</div>
				</div>
			</section>
		);
	}
);
